import { Injectable, signal } from '@angular/core';

export type AppDialogMode = 'confirm' | 'alert';

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Uses danger button for confirm (delete / remove). */
  danger?: boolean;
}

export interface AlertDialogOptions {
  title: string;
  message: string;
  okLabel?: string;
  /** Uses danger styling for the OK button. */
  danger?: boolean;
}

interface DialogState {
  open: boolean;
  mode: AppDialogMode;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger: boolean;
  resolve: ((value: boolean) => void) | null;
}

@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  private readonly state = signal<DialogState>({
    open: false,
    mode: 'confirm',
    title: '',
    message: '',
    confirmLabel: undefined,
    cancelLabel: undefined,
    danger: true,
    resolve: null
  });

  readonly dialog = this.state.asReadonly();

  confirm(options: ConfirmDialogOptions): Promise<boolean> {
    return this.open({
      mode: 'confirm',
      title: options.title,
      message: options.message,
      confirmLabel: options.confirmLabel,
      cancelLabel: options.cancelLabel,
      danger: options.danger ?? true
    });
  }

  /** Single-action notice (errors, warnings, info). */
  alert(options: AlertDialogOptions): Promise<void> {
    return this.open({
      mode: 'alert',
      title: options.title,
      message: options.message,
      confirmLabel: options.okLabel,
      cancelLabel: undefined,
      danger: options.danger ?? false
    }).then(() => undefined);
  }

  accept(): void {
    this.close(true);
  }

  dismiss(): void {
    const { mode } = this.state();
    // Alert: dismiss = acknowledge. Confirm: dismiss = cancel.
    this.close(mode === 'alert');
  }

  private open(options: {
    mode: AppDialogMode;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger: boolean;
  }): Promise<boolean> {
    const previous = this.state().resolve;
    previous?.(false);

    return new Promise<boolean>((resolve) => {
      this.state.set({
        open: true,
        mode: options.mode,
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        danger: options.danger,
        resolve
      });
    });
  }

  private close(result: boolean): void {
    const current = this.state();
    current.resolve?.(result);
    this.state.set({
      open: false,
      mode: 'confirm',
      title: '',
      message: '',
      confirmLabel: undefined,
      cancelLabel: undefined,
      danger: true,
      resolve: null
    });
  }
}
